import { TIER_LABEL, moduleLabel } from '../lib/riskVocabulary';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { Company, BusinessUnit, System, Project } from '../api/types';
import { COUNTRY_OPTIONS } from '../lib/form-options';
import { CreateBuModal, CreateSystemModal, ModalShell } from '../components/org-modals';
import { INDUSTRY_SECTOR, industryHint } from '../lib/mandateLabels';

/**
 * Project creation. Slice 9.6 evolution: inline-create modals for
 * Company/BU/System on the Scope step. Step 2 (D16) removed the
 * "Use sample data" option: customer projects start empty. The backend
 * endpoint POST /projects/:id/seed-from-template remains but no screen
 * calls it.
 *
 * Slice 9.6 also lets the user pick the project level (Company /
 * Business_Unit / System), where previously it was hardcoded to
 * 'Business_Unit'. The level controls which scope ID is required.
 */

// Labels come from the shared vocabulary so the wizard names each dimension
// exactly as the screens and reports do (C-045).
const MODULE_GROUPS: Array<{ title: string; codes: string[] }> = [
  { title: TIER_LABEL.foundation, codes: ['timeline', 'data_risk', 'supply_chain', 'migration_feasibility', 'integrated_foundation'] },
  { title: TIER_LABEL.advanced, codes: ['threat', 'cryptography', 'compliance'] },
  { title: TIER_LABEL.composite, codes: ['integrated_advanced'] },
];

type ProjectLevel = 'Company' | 'Business_Unit' | 'System';

export function ProjectWizard() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const auth = useAuth();
  const [name, setName] = useState('');
  const [projectLevel, setProjectLevel] = useState<ProjectLevel>('Company');
  const [companyId, setCompanyId] = useState('');
  const [buId, setBuId] = useState('');
  const [systemId, setSystemId] = useState('');
  const [copyFrom, setCopyFrom] = useState('');
  // A risk assessment always includes all 9 modules (7 dimensions + 2 composites, C-110): Overall
  // risk and priority are computed from all of them. The engine produces NA for a module that does
  // not apply to an asset (e.g. supply_chain on a non-vendor asset), so this is always safe.
  const moduleScope: Record<string, boolean> = {
    timeline: true,
    data_risk: true,
    supply_chain: true,
    migration_feasibility: true,
    integrated_foundation: true,
    threat: true,
    cryptography: true,
    compliance: true,
    integrated_advanced: true,
  };

  // Modal state — which entity we're creating, if any
  const [modal, setModal] = useState<null | 'company' | 'bu' | 'system'>(null);

  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: () => api.get<Company[]>('/companies'),
  });

  const { data: businessUnits = [] } = useQuery({
    queryKey: ['business-units', companyId],
    queryFn: () => api.get<BusinessUnit[]>(`/business-units?company_id=${companyId}`),
    enabled: companyId !== '',
  });

  const { data: systems = [] } = useQuery({
    queryKey: ['systems', buId],
    queryFn: () => api.get<System[]>(`/systems?business_unit_id=${buId}`),
    enabled: buId !== '',
  });

  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<Project[]>('/projects?limit=200'),
  });

  const isAdmin = auth.hasRole('admin');
  const isAnalyst = auth.hasRole('analyst');
  const canCreateCompany = isAdmin;
  const canCreateBuOrSystem = isAdmin || isAnalyst;

  const createProject = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        name,
        project_level: projectLevel,
        scope_company_id: companyId,
        module_scope: moduleScope,
      };
      if (projectLevel === 'Business_Unit' || projectLevel === 'System') {
        body['scope_business_unit_id'] = buId;
      }
      if (projectLevel === 'System') {
        body['scope_system_id'] = systemId;
      }
      const project = await api.post<Project>('/projects', body);
      if (copyFrom) await api.post(`/projects/${project.id}/copy-settings`, { source_project_id: copyFrom });
      return project;
    },
    onSuccess: (project) => {
      navigate(`/projects/${project.id}`);
    },
  });

  const canSubmit =
    name.trim() !== '' &&
    companyId !== '' &&
    (projectLevel === 'Company' || buId !== '') &&
    (projectLevel !== 'System' || systemId !== '');

  return (
    <div className="max-w-3xl mx-auto px-6 py-6">
      <h1 className="text-3xl mb-8">New project</h1>

      <Section step={1} title="Identity">
        <div className="space-y-4">
          <div>
            <label htmlFor="name">Project name</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Retail Banking Q3 2026 PQC Assessment"
              className="w-full"
            />
          </div>
          <div>
            <label htmlFor="copy-from">Copy settings from (optional)</label>
            <select id="copy-from" value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)} className="w-full" data-testid="copy-from">
              <option value="">Start with default settings</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <p className="text-xs text-ink-500 mt-1">
              Copies the column mapping, reporting profile, report settings, members, policies, vendor commitments,
              readiness answers and programme plan. Assets are not copied: import them into the new project.
            </p>
          </div>
        </div>
      </Section>

      <Section step={2} title="Scope">
        <p className="text-sm text-ink-500 mb-3">
          Select the level this project assesses, then pick the company,
          business unit, and (optionally) system.
        </p>

        <div className="mb-4">
          <label htmlFor="level">Project level</label>
          <select
            id="level"
            value={projectLevel}
            onChange={(e) => {
              setProjectLevel(e.target.value as ProjectLevel);
              // Reset child selections when level changes
              if (e.target.value === 'Company') { setBuId(''); setSystemId(''); }
              if (e.target.value === 'Business_Unit') setSystemId('');
            }}
            className="w-full"
          >
            <option value="Company">Company-level (covers all BUs)</option>
            <option value="Business_Unit">Business unit-level</option>
            <option value="System">System-level (single system)</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <DropdownWithCreate
            id="company"
            label="Company"
            value={companyId}
            onChange={(v) => { setCompanyId(v); setBuId(''); setSystemId(''); }}
            options={companies.map((c) => ({ id: c.id, name: c.name }))}
            placeholder="Select company…"
            onCreateClick={canCreateCompany ? () => setModal('company') : undefined}
          />
          <DropdownWithCreate
            id="bu"
            label="Business unit"
            value={buId}
            onChange={(v) => { setBuId(v); setSystemId(''); }}
            options={businessUnits.map((b) => ({ id: b.id, name: b.name }))}
            placeholder="Select business unit…"
            disabled={companyId === '' || projectLevel === 'Company'}
            onCreateClick={canCreateBuOrSystem && companyId !== '' && projectLevel !== 'Company' ? () => setModal('bu') : undefined}
          />
        </div>
        {projectLevel !== 'System' && (
          <p className="text-xs text-ink-500 mt-2" data-testid="org-structure-hint">
            {projectLevel === 'Company' ? 'Business units and systems' : 'Systems'} are added after the project is created, in the project's Settings → Business units and systems.
          </p>
        )}

        {projectLevel === 'System' && (
          <div className="mt-4">
            <DropdownWithCreate
              id="system"
              label="System"
              value={systemId}
              onChange={setSystemId}
              options={systems.map((s) => ({ id: s.id, name: s.name }))}
              placeholder="Select system…"
              disabled={buId === ''}
              onCreateClick={canCreateBuOrSystem && buId !== '' ? () => setModal('system') : undefined}
            />
          </div>
        )}
      </Section>

      <Section step={3} title="Modules">
        <p className="text-sm text-ink-500 mb-3" data-testid="wizard-modules-why">
          A risk assessment always scores all seven dimensions and both composites: Overall risk
          and each asset&apos;s priority are calculated from all of them, so none can be left out.
          A dimension that does not apply to an asset is left out of that asset&apos;s scores.
        </p>
        {MODULE_GROUPS.map((g, gi) => (
          <div key={g.title}>
            <h3 className={`text-xs font-medium text-ink-500 uppercase tracking-wider ${gi === 0 ? 'mt-4' : 'mt-5'} mb-2`}>{g.title}</h3>
            <ul className="space-y-2">
              {g.codes.map((code) => (
                <li key={code} className="flex items-center gap-3" data-testid={`mod-${code}`}>
                  <span aria-hidden className="inline-flex h-4 w-4 items-center justify-center rounded-sm bg-ink-900 text-white text-[10px]">✓</span>
                  <span>{moduleLabel(code)}</span>
                  <span className="text-xs text-ink-400">Included</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </Section>

      <Section step={4} title="Review and create">
        <dl className="text-sm space-y-2">
          <Row label="Name" value={name || <em className="text-ink-300">— required</em>} />
          <Row
            label="Scope"
            value={
              <>
                <span className="font-mono text-xs text-ink-400">{projectLevel}</span>
                {' · '}
                {companies.find((c) => c.id === companyId)?.name || <em className="text-ink-300">—</em>}
                {buId && ' / '}
                {businessUnits.find((b) => b.id === buId)?.name}
                {systemId && ' / '}
                {systems.find((s) => s.id === systemId)?.name}
              </>
            }
          />
          {copyFrom && <Row label="Settings from" value={projects.find((p) => p.id === copyFrom)?.name} />}
          <Row
            label="Modules"
            value={Object.entries(moduleScope).filter(([, v]) => v).map(([k]) => moduleLabel(k)).join(', ')}
          />
        </dl>

        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={() => createProject.mutate()}
            disabled={!canSubmit || createProject.isPending}
            className="btn-primary"
          >
            {createProject.isPending ? 'Creating…' : 'Create project'}
          </button>
          {createProject.error && (
            <span className="text-accent text-sm self-center">
              {(createProject.error as Error).message}
            </span>
          )}
        </div>
      </Section>

      {/* Modals — render last so they layer above the form */}
      {modal === 'company' && (
        <CreateCompanyModal
          onClose={() => setModal(null)}
          onCreated={(c) => {
            qc.invalidateQueries({ queryKey: ['companies'] });
            setCompanyId(c.id);
            setModal(null);
          }}
        />
      )}
      {modal === 'bu' && companyId && (
        <CreateBuModal
          companyId={companyId}
          onClose={() => setModal(null)}
          onCreated={(b) => {
            qc.invalidateQueries({ queryKey: ['business-units', companyId] });
            // Only auto-select if level actually uses a BU selection
            if (projectLevel !== 'Company') setBuId(b.id);
            setModal(null);
          }}
        />
      )}
      {modal === 'system' && buId && (
        <CreateSystemModal
          buId={buId}
          onClose={() => setModal(null)}
          onCreated={(s) => {
            qc.invalidateQueries({ queryKey: ['systems', buId] });
            // Only auto-select if System-level (single system); at Company/BU
            // level the system exists in DB but doesn't need to be "selected"
            if (projectLevel === 'System') setSystemId(s.id);
            setModal(null);
          }}
        />
      )}
    </div>
  );
}

// ----------------------------- subcomponents -----------------------------

function Section({ step, title, children }: {
  step: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-8 pb-8 border-b border-ink-200 last:border-0">
      <h2 className="text-xl mb-3 flex items-baseline gap-3">
        <span className="text-ink-300 font-mono text-sm">{String(step).padStart(2, '0')}</span>
        {title}
      </h2>
      {children}
    </section>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      <dt className="text-ink-500">{label}</dt>
      <dd className="col-span-2">{value}</dd>
    </div>
  );
}

function DropdownWithCreate({
  id, label, value, onChange, options, placeholder, disabled, onCreateClick,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ id: string; name: string }>;
  placeholder: string;
  disabled?: boolean;
  onCreateClick?: () => void;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label htmlFor={id}>{label}</label>
        {onCreateClick && (
          <button
            type="button"
            onClick={onCreateClick}
            className="text-xs text-accent hover:underline"
          >
            + New
          </button>
        )}
      </div>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full disabled:bg-ink-50"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
    </div>
  );
}

// ----------------------------- modals -----------------------------

function CreateCompanyModal({ onClose, onCreated }: {
  onClose: () => void;
  onCreated: (c: Company) => void;
}) {
  const [name, setName] = useState('');
  const [shortCode, setShortCode] = useState('');
  // No default: the industry decides which sector regulators' PQC instruments apply, so the user picks it.
  const [industry, setIndustry] = useState('');
  const [country, setCountry] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [countryOpen, setCountryOpen] = useState(false);
  const [dataResidencyRegion, setDataResidencyRegion] = useState('');
  const filteredCountries = COUNTRY_OPTIONS.filter((c) =>
    c.toLowerCase().includes(countrySearch.toLowerCase()),
  );
  const mut = useMutation({
    mutationFn: () => api.post<Company>('/companies', {
      name,
      short_code: shortCode,
      industry_type: industry,
      country,
      data_residency_region: dataResidencyRegion.trim() === '' ? null : dataResidencyRegion.trim(),
    }),
    onSuccess: onCreated,
  });
  // industry_type is required server-side (CreateCompanySchema: min(1)), so it
  // must be part of the gate — it defaults to 'Technology' but the user can
  // clear the field, which previously produced a 400 on submit.
  const canSubmit =
    name.trim() !== '' && shortCode.trim() !== '' && industry.trim() !== '' && country !== '';
  return (
    <ModalShell title="New company" onClose={onClose}>
      <div className="space-y-3 text-sm">
        <div>
          <label htmlFor="co-name">Name</label>
          <input id="co-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full" placeholder="Acme Corp" />
        </div>
        <div>
          <label htmlFor="co-short">Short code</label>
          <input id="co-short" value={shortCode} onChange={(e) => setShortCode(e.target.value)} className="w-full" placeholder="acme" />
          <p className="text-xs text-ink-400 mt-1">Lowercase identifier, used in URLs and exports.</p>
        </div>
        <div>
          <label htmlFor="co-ind">Industry</label>
          <input id="co-ind" list="co-ind-options" value={industry} onChange={(e) => setIndustry(e.target.value)} className="w-full" placeholder="Pick or type, e.g. Banking" />
          <datalist id="co-ind-options">
            {Object.keys(INDUSTRY_SECTOR).map((i) => <option key={i} value={i} />)}
          </datalist>
          <p className="text-xs text-ink-400 mt-1" data-testid="industry-sector-hint">
            {industryHint(industry)}
          </p>
        </div>
        <div className="relative">
          <label htmlFor="co-country-search">Country</label>
          <div
            className="w-full border rounded px-2 py-1 cursor-pointer flex justify-between items-center bg-white"
            onClick={() => setCountryOpen((o) => !o)}
          >
            <span className={country ? '' : 'text-ink-400'}>
              {country || 'Select country…'}
            </span>
            <span className="text-ink-400 text-xs">▼</span>
          </div>
          {countryOpen && (
            <div className="absolute z-50 mt-1 w-full bg-white border rounded shadow-lg">
              <input
                id="co-country-search"
                autoFocus
                value={countrySearch}
                onChange={(e) => setCountrySearch(e.target.value)}
                placeholder="Search…"
                className="w-full px-2 py-1 border-b text-sm"
                onClick={(e) => e.stopPropagation()}
              />
              <ul className="max-h-48 overflow-y-auto text-sm">
                {filteredCountries.length === 0 && (
                  <li className="px-2 py-1 text-ink-400">No matches</li>
                )}
                {filteredCountries.map((c) => (
                  <li
                    key={c}
                    className={`px-2 py-1 cursor-pointer hover:bg-ink-50 ${c === country ? 'font-medium' : ''}`}
                    onClick={() => { setCountry(c); setCountryOpen(false); setCountrySearch(''); }}
                  >
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div>
          <label htmlFor="co-residency">Data residency region</label>
          <input id="co-residency" value={dataResidencyRegion} onChange={(e) => setDataResidencyRegion(e.target.value)} maxLength={100} className="w-full" placeholder="e.g. EU, India, US (optional)" />
          <p className="text-xs text-ink-400 mt-1">Where this company's data must legally reside. Optional.</p>
        </div>
      </div>
      {mut.error && <p className="text-accent text-sm mt-3">{(mut.error as Error).message}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
        <button type="button" disabled={!canSubmit || mut.isPending} onClick={() => mut.mutate()} className="btn-primary">
          {mut.isPending ? 'Creating…' : 'Create company'}
        </button>
      </div>
    </ModalShell>
  );
}
